import React, { useEffect, useState, useContext, useMemo } from "react";
import { RoleContext } from "../../contexts/RoleContext";
import { AuthContext } from "../../contexts/AuthContext";
import { useParams, useNavigate } from "react-router-dom";
import Notification from "../../components/UI/Notification";
import RolePermissionsForm from "./components/RolePermissionsForm";
import { getRoleBasedRoute } from "../../utils/routeUtils";
import { FaArrowLeft } from "react-icons/fa";

const EditRole = () => {
  const { id: roleId } = useParams();
  const navigate = useNavigate();
  const { token, roles: userRoles } = useContext(AuthContext);
  const { currentRole, fetchRoleDetails, editRole, clearError } =
    useContext(RoleContext);

  const [hasFetched, setHasFetched] = useState(false);
  const [notification, setNotification] = useState(null);

  const getRoleBasedRouteLocal = (route) => {
    if (!userRoles || userRoles.length === 0) return route;
    return getRoleBasedRoute(route, userRoles);
  };

  useEffect(() => {
    const fetchDetails = async () => {
      if (!hasFetched && token && roleId) {
        try {
          clearError();
          await fetchRoleDetails(token, roleId);
          setHasFetched(true);
        } catch (err) {
          setNotification({
            message: `Error fetching role details: ${err.message}`,
            type: "error",
          });
        }
      }
    };
    fetchDetails();
  }, [token, roleId, hasFetched, fetchRoleDetails, clearError]);

  // Convert array of permissions to the flattened object format { "perm_key": true }
  const formattedInitialPermissions = useMemo(() => {
    if (!currentRole) return {};

    // Handle case where currentRole is the array of permissions directly
    // or currentRole.permissions is the array
    const permsArray = Array.isArray(currentRole)
      ? currentRole
      : Array.isArray(currentRole.permissions)
        ? currentRole.permissions
        : [];

    return permsArray.reduce((acc, perm) => {
      // Handle string permissions or object permissions with a 'key' or 'name'
      const key = typeof perm === "string" ? perm : perm.key || perm.name;
      if (key) acc[key] = true;
      return acc;
    }, {});
  }, [currentRole]);

  const handleUpdateRoleWithPermissions = async (data) => {
    clearError();
    try {
      // Calculate diff: new additions vs removals
      const newlyAdded = [];
      const newlyRemoved = [];

      // Check all possible permissions from the form state
      Object.entries(data.permissions || {}).forEach(([key, isChecked]) => {
        const wasOriginallyChecked = !!formattedInitialPermissions[key];

        if (isChecked && !wasOriginallyChecked) {
          // Newly added
          newlyAdded.push(key);
        } else if (!isChecked && wasOriginallyChecked) {
          // Newly removed
          newlyRemoved.push(key);
        }
      });

      const payload = {
        add: newlyAdded,
        remove: newlyRemoved,
      };

      const response = await editRole(token, roleId, payload);
      if (response?.success) {
        navigate(getRoleBasedRouteLocal("/admin/roles"), {
          state: { successMessage: "Role updated successfully." },
        });
      } else {
        setNotification({
          message: response?.message || "Failed to update role.",
          type: "error",
        });
      }
    } catch (err) {
      setNotification({
        message: `Failed to update role: ${err.message}`,
        type: "error",
      });
    }
  };

  if ((!hasFetched || !currentRole) && !notification) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // If we have an error and no role, show the error with a back button
  if (!currentRole && notification) {
    return (
      <div className="w-full p-8 text-center">
        <Notification
          message={notification.message}
          type="error"
          onClose={() => setNotification(null)}
        />
        <button
          onClick={() => navigate(getRoleBasedRouteLocal("/admin/roles"))}
          className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg"
        >
          Back to Roles
        </button>
      </div>
    );
  }

  return (
    <div className="w-full">
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}
      <div className="p-4 md:p-6 lg:p-8 max-w-[1500px] mx-auto">
        <button
          type="button"
          onClick={() => navigate(getRoleBasedRouteLocal("/admin/roles"))}
          className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 font-medium mb-6 transition-colors group"
        >
          <FaArrowLeft className="group-hover:-translate-x-1 transition-transform" />
          Back to Roles
        </button>
        <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-6">
          Edit Role Permissions
        </h2>

        <RolePermissionsForm
          type="Role"
          initialName={currentRole.name || currentRole.role || ""}
          initialPermissions={formattedInitialPermissions}
          onSave={handleUpdateRoleWithPermissions}
          onCancel={() => navigate(getRoleBasedRouteLocal("/admin/roles"))}
        />
      </div>
    </div>
  );
};

export default EditRole;
